/* v3.21-1667 · Funded — READ-ONLY notification screen.
 *
 * Team-facing view of incoming funding notifications resolved to Hub
 * clients by exact Client ID.  Posts a Green Activity note on the
 * matched client via POST /api/finturf-funded/apply/{identifier}.
 * NEVER modifies Deal financial fields — the team updates Funding
 * Completion (status, funded_date, funded_amount) manually after
 * reviewing the client file.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Loader2, RefreshCw, Search, ExternalLink, Link2, ShieldCheck,
  Landmark, ArrowUpRight, AlertCircle, CheckCircle2, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatET } from '../utils/timeUtils';

const fmt$ = (n) => (typeof n === 'number' && Number.isFinite(n))
  ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—';

export default function FundedPage() {
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData]           = useState(null);
  const [error, setError]         = useState(null);

  const [matchFilter, setMatchFilter] = useState('all');
  const [q, setQ]                     = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [lender, setLender]           = useState('all');

  const [associateOpen, setAssociateOpen] = useState(null); // identifier
  const [associateClientId, setAssociateClientId] = useState('');
  const [associating, setAssociating] = useState(false);

  // v3.21-1667 · Apply Funding modal state
  const [applyRow, setApplyRow]           = useState(null);   // full Finturf row
  const [applySearch, setApplySearch]     = useState('');
  const [applyResults, setApplyResults]   = useState([]);
  const [applySelected, setApplySelected] = useState(null);   // chosen Hub client
  const [applySearching, setApplySearching] = useState(false);
  const [applyBusy, setApplyBusy]         = useState(false);
  const [applyError, setApplyError]       = useState(null);
  const applyLockRef                      = useRef(false);   // double-click guard

  const navigate = useNavigate();

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setError(null);
    try {
      const params = { limit: 500 };
      // 'review' filter (frontend synthetic) → include ambiguous + conflict
      if (matchFilter === 'review') {
        params.match = 'ambiguous';   // primary; UI merges conflict client-side
      } else if (matchFilter !== 'all') {
        params.match = matchFilter;
      }
      if (q)        params.q     = q;
      if (dateFrom) params.from  = dateFrom;
      if (dateTo)   params.to    = dateTo;
      if (lender && lender !== 'all') params.lender = lender;
      const res = await api.get('/finturf-funded/list', { params });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Refetch on filter change (debounced by keystroke via a small guard).
  useEffect(() => {
    const t = setTimeout(() => { if (!loading) load(true); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [matchFilter, q, dateFrom, dateTo, lender]);

  const lenders = useMemo(() =>
    (data?.facets?.sources || []).map(([name]) => name).filter(Boolean),
    [data]);

  const associate = async () => {
    if (!associateClientId?.trim() || !associateOpen) return;
    setAssociating(true);
    try {
      await api.post(`/finturf/associate/${associateOpen}`,
                     null,
                     { params: { client_id: associateClientId.trim() } });
      toast.success(`Associated ${associateOpen} with client`);
      setAssociateOpen(null); setAssociateClientId('');
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Association failed');
    } finally {
      setAssociating(false);
    }
  };

  // ── v3.21-1667 · Apply Funding workflow ─────────────────────
  const openApply = (row) => {
    setApplyRow(row);
    setApplySearch(row.hub_client_name || '');
    setApplyResults([]);
    // v3.21-1667 (post-correction) · When the row is deterministically
    // matched by exact Client ID, preselect the resolved client.  Never
    // preselect for Ambiguous / Conflict rows — those require review.
    setApplySelected(row.hub_match === 'matched' ? {
      id: row.hub_client_id,
      full_name: row.hub_client_name,
      deal_number: row.hub_deal_number,
      company_id: row.hub_company_id,
      credit_app_confirmation_number: null,
      funded_amount: 0,
      deal_amount: row.approved_amount || 0,
      funding_status: 'Pending Deposit',
    } : null);
    setApplyError(null);
  };
  const closeApply = () => {
    setApplyRow(null); setApplySearch(''); setApplyResults([]);
    setApplySelected(null); setApplyError(null); applyLockRef.current = false;
  };

  useEffect(() => {
    if (!applyRow || applySelected) return;
    if (!applySearch || applySearch.trim().length < 2) {
      setApplyResults([]); return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setApplySearching(true);
      try {
        const res = await api.get('/finturf-funded/search-clients', { params: { q: applySearch, limit: 10 } });
        if (!cancelled) setApplyResults(res.data?.results || []);
      } catch (e) {
        if (!cancelled) setApplyResults([]);
      } finally {
        if (!cancelled) setApplySearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [applySearch, applyRow, applySelected]);

  const doApply = async () => {
    if (!applyRow || !applySelected) return;
    if (applyLockRef.current || applyBusy) return; // idempotent guard
    applyLockRef.current = true;
    setApplyBusy(true); setApplyError(null);
    try {
      const res = await api.post(
        `/finturf-funded/apply/${applyRow.identifier}`,
        { client_id: applySelected.id, expected_funded_amount: applyRow.funded_amount },
      );
      if (res.data?.duplicate) {
        toast.info('Already posted for this application. No duplicate note created.');
      } else {
        toast.success('Green Funding Update note posted. Funding Completion unchanged.');
      }
      closeApply();
      await load(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || 'Apply Funding failed');
      setApplyError(msg);
      applyLockRef.current = false;
    } finally {
      setApplyBusy(false);
    }
  };

  const applyPreview = useMemo(() => {
    if (!applyRow || !applySelected) return null;
    const prior = Number(applySelected.funded_amount || 0);
    const contrib = Number(applyRow.funded_amount || 0);
    const dealAmt = Number(applySelected.deal_amount || 0);
    const newFunded = Math.round((prior + contrib) * 100) / 100;
    let newStatus = 'Pending Deposit';
    if (dealAmt > 0 && newFunded >= dealAmt - 0.005) newStatus = 'Funded';
    else if (newFunded > 0) newStatus = 'Partially Funded';
    return { prior, contrib, newFunded, dealAmt, newStatus };
  }, [applyRow, applySelected]);

  return (
    <div className="space-y-4 p-6" data-testid="funded-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <Landmark className="h-6 w-6 text-emerald-600" />
            Funded
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Funded client activity and funding notifications. Matched to Hub
            clients by exact Client&nbsp;ID. Posting a funding update records
            a Green Activity note on the client — the Funding Completion
            block (Status / Funded Date / Funded Amount) remains manually
            edited by authorised staff.
          </p>
        </div>
        <Button
          onClick={() => load()}
          disabled={refreshing || loading}
          variant="outline"
          data-testid="funded-refresh-btn"
        >
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh live data
        </Button>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="funded-summary">
        <Card><CardContent className="p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Total Funded Applications</div>
          <div className="text-2xl font-bold text-primary mt-1" data-testid="funded-count">
            {data?.funded_total ?? '—'}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">of {data?.total_examined ?? '—'} examined</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Total Funded Amount</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="funded-amount-total">
            {fmt$(data?.total_funded_amount)}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Matched to Client</div>
          <div className="text-2xl font-bold text-blue-700 mt-1" data-testid="funded-matched-count">
            {data?.matched_count ?? '—'}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">by exact Client&nbsp;ID</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Unmatched</div>
          <div className="text-2xl font-bold text-amber-700 mt-1" data-testid="funded-unmatched-count">
            {data?.unmatched_count ?? '—'}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Needs review</div>
          <div className="text-2xl font-bold text-rose-700 mt-1" data-testid="funded-conflict-count">
            {((data?.ambiguous_count ?? 0) + (data?.conflict_count ?? 0)) || '—'}
          </div>
        </CardContent></Card>
      </div>

      {/* ── Filters ── */}
      <Card><CardContent className="p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex gap-2 items-center flex-wrap">
          {['all', 'matched', 'unmatched', 'review'].map((v) => (
            <Button
              key={v}
              size="sm"
              variant={matchFilter === v ? 'default' : 'outline'}
              onClick={() => setMatchFilter(v)}
              data-testid={`funded-filter-${v}`}
            >
              {v === 'review' ? 'Needs review' : v[0].toUpperCase() + v.slice(1)}
            </Button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Search client, Application ID, or application #…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="funded-search-input"
          />
        </div>
        <Input
          type="date"
          className="h-8 text-sm w-[150px]"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          data-testid="funded-date-from"
        />
        <Input
          type="date"
          className="h-8 text-sm w-[150px]"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          data-testid="funded-date-to"
        />
      </CardContent></Card>

      {/* ── Notice: activity-only, no Deal writes ── */}
      <Card className="border-blue-200 bg-blue-50/40" data-testid="funded-notice">
        <CardContent className="p-3 text-xs text-blue-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            Posting a funding update records a Green Activity note on the
            client — it never touches Funding Completion (<code>funded_amount</code>,
            <code>funded_date</code>, <code>funding_completion_status</code>,
            <code>paid_in_full</code>, <code>all_parties_settled</code>).
            The team decides how to update the Deal financials after
            reviewing the full client picture.
          </div>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-700 text-sm" data-testid="funded-error">{error}</div>
          ) : !data?.rows?.length ? (
            <div className="p-12 text-center text-slate-500 text-sm" data-testid="funded-empty">
              No {matchFilter === 'all' ? '' : matchFilter + ' '}funding notifications for the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70">
                  <tr className="border-b border-slate-200">
                    {['Client', 'Client ID', 'Funded Amount', 'Last Updated', 'Match', 'Action'].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-[11px] font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.identifier}
                        className="border-b border-slate-100 hover:bg-emerald-50/30"
                        data-testid={`funded-row-${r.identifier}`}>
                      <td className="py-2 px-3">
                        <div className="text-slate-800 font-medium">
                          {r.hub_client_name || <span className="text-slate-400 italic">unassociated</span>}
                        </div>
                      </td>
                      <td className="py-2 px-3 font-mono text-slate-700">
                        {r.hub_deal_number || r.identifier}
                      </td>
                      <td className="py-2 px-3 font-semibold text-emerald-700" data-testid={`funded-amount-${r.identifier}`}>
                        {fmt$(r.funded_amount)}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">
                        {r.updated_at ? formatET(r.updated_at.replace(' ', 'T') + 'Z', 'MMM d, yyyy · h:mm a') : '—'}
                      </td>
                      <td className="py-2 px-3">
                        {r.hub_match === 'matched' ? (
                          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 text-[10px]" data-testid={`funded-badge-${r.identifier}`}>
                            Matched
                          </Badge>
                        ) : r.hub_match === 'ambiguous' || r.hub_match === 'conflict' ? (
                          <Badge variant="outline" className="border-rose-300 text-rose-700 bg-rose-50 text-[10px]" data-testid={`funded-badge-${r.identifier}`}>
                            Needs review
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[10px]" data-testid={`funded-badge-${r.identifier}`}>
                            Unmatched
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {r.hub_match === 'matched' ? (
                          <div className="flex items-center gap-2 justify-end">
                            <Link
                              to={`/clients/${r.hub_client_id}`}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium"
                              data-testid={`funded-open-deal-${r.identifier}`}
                            >
                              Open Client <ArrowUpRight className="h-3 w-3" />
                            </Link>
                            <Button
                              size="sm"
                              className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => openApply(r)}
                              data-testid={`funded-apply-btn-${r.identifier}`}
                            >
                              Post Funding Update
                            </Button>
                          </div>
                        ) : r.hub_match === 'ambiguous' || r.hub_match === 'conflict' ? (
                          <div className="flex items-center gap-2 justify-end">
                            <span className="text-[10px] text-rose-700 italic" data-testid={`funded-review-${r.identifier}`}>
                              Internal review required
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 justify-end">
                            <Button
                              size="sm"
                              className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => openApply(r)}
                              data-testid={`funded-apply-btn-${r.identifier}`}
                            >
                              <ArrowRight className="h-3 w-3 mr-1" /> Associate &amp; Post
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* v3.21-1667 (post-final) · Post Funding Update modal */}
      <Dialog open={!!applyRow} onOpenChange={(o) => !o && closeApply()}>
        <DialogContent className="max-w-2xl" data-testid="apply-funding-modal">
          <DialogHeader>
            <DialogTitle>Post Funding Update</DialogTitle>
            <DialogDescription>
              Records a Green Activity note on the resolved Hub client.
              Does <strong>not</strong> change <code>funded_amount</code>,
              <code>funding_status</code>, <code>funded_date</code>,
              <code>paid_in_full</code>, or <code>all_parties_settled</code>.
              The team updates Funding Completion manually after review.
            </DialogDescription>
          </DialogHeader>

          {applyRow && (
            <div className="space-y-4">
              {/* Funding summary — no provider / lender / product / status */}
              <Card className="border-emerald-200 bg-emerald-50/40">
                <CardContent className="p-3 grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Application ID:</span> <span className="font-mono">{applyRow.hub_deal_number || applyRow.identifier}</span></div>
                  <div><span className="text-slate-500">Funded Amount:</span> <strong className="text-emerald-700">{fmt$(applyRow.funded_amount)}</strong></div>
                  <div><span className="text-slate-500">Last Updated:</span> {applyRow.updated_at || '—'}</div>
                </CardContent>
              </Card>

              {/* Client search */}
              {!applySelected ? (
                <div className="space-y-2">
                  <label className="text-xs text-slate-500">Search Hub client (min 2 chars):</label>
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      className="pl-8 h-8 text-sm"
                      placeholder="Client name or Application ID"
                      value={applySearch}
                      onChange={(e) => setApplySearch(e.target.value)}
                      autoFocus
                      data-testid="apply-search-input"
                    />
                  </div>
                  {applySearching ? (
                    <div className="text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Searching…</div>
                  ) : applyResults.length === 0 && applySearch.length >= 2 ? (
                    <div className="text-xs text-slate-500">No matching clients.</div>
                  ) : (
                    <div className="max-h-56 overflow-y-auto border rounded divide-y">
                      {applyResults.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => setApplySelected(r)}
                          className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-xs"
                          data-testid={`apply-result-${r.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-slate-800">{r.full_name || '—'}</div>
                              <div className="text-[10px] text-slate-500">
                                Client ID: <span className="font-mono">{r.deal_number || '—'}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Confirmation summary — activity-only, no financial mutation */
                <Card className="border-blue-200 bg-blue-50/40">
                  <CardContent className="p-3 space-y-2 text-xs" data-testid="apply-confirm-summary">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Selected Client:</span>
                      <strong>{applySelected.full_name}</strong>
                    </div>
                    <div className="flex justify-between" data-testid="apply-confirm-client-id">
                      <span className="text-slate-500">Application ID:</span>
                      <span className="font-mono">{applySelected.deal_number || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Funded Amount (from notification):</span>
                      <strong className="text-emerald-700">{fmt$(applyRow.funded_amount)}</strong>
                    </div>
                    <div className="border-t pt-1 text-[11px] text-slate-600">
                      A Green <em>Funding Update</em> note will be posted on this client.
                      Funding Completion (Status / Funded Date / Funded Amount) is NOT modified.
                    </div>
                    <div className="text-[10px] text-slate-500 pt-1 flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" />
                      <span>Deal financial fields remain unchanged.</span>
                    </div>
                    <button
                      className="text-[10px] text-blue-600 hover:text-blue-700 underline"
                      onClick={() => setApplySelected(null)}
                      data-testid="apply-change-client"
                    >
                      Change client
                    </button>
                  </CardContent>
                </Card>
              )}

              {applyError && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="apply-error">
                  {applyError}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeApply} disabled={applyBusy}>Cancel</Button>
            <Button
              onClick={doApply}
              disabled={!applySelected || applyBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="apply-funding-submit"
            >
              {applyBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Post Funding Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
